import { isGroupRow } from '../../stores/utils/group-row-guards';
import { parseWhatsNext } from '../../components/table/node-property-panel/parse-whats-next';
// TYPE-ONLY, same reason `canvas-view-model.ts` imports it this way — see
// that file's comment.
import type { TableNode } from '../../stores/table-store';

/**
 * Build Stage canvas, Phase 2 — agent-to-agent edges.
 *
 * Edge derivation is NOT a one-hop `destination → owning group` lookup.
 * Routing data lives in three places (rich-asset option destinations are out
 * of scope — their shape is unconfirmed):
 *
 *   - `nextNodes`: the default/sequential pointer most rows carry — produces
 *     `flow` edges.
 *   - `whatsNext`: branching (`label~nodeNumber` pairs) — a MANAGED branch
 *     (its label matches one of the node's `input_vars` intent names)
 *     produces an `intent` edge; any other branch (error handling, a
 *     `false` fallback) produces an `error` edge.
 *   - A Supervisor routing node's Parameter Input (`parseSupervisorRouting`)
 *     — every branch there is a routed Supervisor outcome, so each produces
 *     an `intent` edge labelled with its key.
 *
 * A branch can land on a node still owned by the SAME group before the
 * chain eventually reaches a different agent — real solution data confirms
 * this (Protolab's own fixture edges carry a `hops` array of intermediate
 * same-group transitions). So resolving a destination means WALKING the
 * chain via each intermediate node's own `nextNodes` pointer until it
 * crosses into a different group, not reading one field and stopping. A
 * same-group destination (the walk never crosses out) produces no edge —
 * matches Protolab's fixture, which has zero self-loops.
 *
 * The walk can also end on an UNGROUPED node rather than a real group —
 * Bobcat!'s own `SysSupervisorRouting` data has real examples (error/
 * live-agent/frustration destinations land on ungrouped nodes). That always
 * produces no edge, regardless of the ungrouped node's type — there is no
 * real group to draw a card-to-card connection to or from.
 *
 * REVISED 2026-09-23: this used to special-case an ungrouped ACTION
 * destination, routing it to a synthetic "Use-case Agents" aggregate card.
 * That card no longer exists — see `canvas-view-model.ts`'s revision note; a
 * Use Case Agent is a real group, so a real one is resolved the same way any
 * other group is, via the `'group'` branch below, with no special case
 * needed.
 */

export type CanvasEdgeKind = 'flow' | 'intent' | 'error';

export interface CanvasEdge {
  /** Stable across renders: `${source}->${target}:${kind}:${label}`. */
  id: string;
  /** Source/destination group row ids — the canvas node ids they connect. */
  source: string;
  target: string;
  kind: CanvasEdgeKind;
  /** The `whatsNext` branch label, for `intent`/`error` edges only. */
  label?: string;
  /**
   * Not a stored route: the Supervisor reaches this agent by matching what a
   * customer says against a Decision node's example phrases. See
   * `addPhraseMatchedEdges`.
   */
  inferred?: boolean;
}

// A same-group hop chain that never reaches another agent (a "back to main
// menu" style loop) must not spin forever — this is generous for any real
// solution's chain length while still bounding a corrupt-data cycle.
const MAX_WALK_HOPS = 50;

/** A row's owning group id: itself, if it IS a group header; its parent's, if it's a member; otherwise none. */
const groupIdOf = (row: TableNode): string | null =>
  isGroupRow(row) ? row.id : (row.settings?.groupId ?? null);

/**
 * The `input_vars` names a `SysMultiMatchRouting` node's Parameter Input
 * declares — same source `RoutingIntentsField.parseInputVars` reads, kept
 * separate rather than imported: that helper takes the raw JSON *string*
 * `ParameterInputField` hands it, while a row's `parameterInput` here is
 * already the parsed object — the two shapes don't line up cleanly enough
 * to share one function.
 */
const managedIntentNames = (row: TableNode): Set<string> => {
  const raw = row.parameterInput as Record<string, unknown> | undefined;
  const inputVars = typeof raw?.input_vars === 'string' ? raw.input_vars : '';
  return new Set(
    inputVars
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean)
  );
};

/**
 * The stored `command` on real solutions is `SupervisorRouting` (checked live
 * on Bobcat! and an onboarding-created solution, node 7030); the platform's
 * action-node list names the same function `SysSupervisorRouting`. Accept
 * both rather than guessing which one a given solution was saved with.
 */
const SUPERVISOR_ROUTING_COMMANDS = new Set([
  'SupervisorRouting',
  'SysSupervisorRouting',
]);

/** `parameterInput` arrives as a parsed object or as a JSON string. */
const asRecord = (raw: unknown): Record<string, unknown> | null => {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

export interface SupervisorRoutingBranch {
  label: string;
  /** The destination node number, as a string (the resolver's key type). */
  destination: string;
}

const DISAMBIGUATION_LISTS = [
  'intent_disambiguation',
  'menu_option_disambiguation',
] as const;

/**
 * A Supervisor routing node's branches: every `destination_nodes` key (label
 * → node number) plus both `static_listpicker_text` disambiguation lists
 * (`{ destination_node, label }` entries). The node's other keys
 * (`nlx_entry_node`, `last_menu_selection_var`, `supervisor_output`) are
 * metadata and variable names, never destinations.
 *
 * Several labels routinely share one destination (Bobcat!'s `chit_chat`,
 * `out_of_scope` and `profanity` all go to 7300) — each stays its own branch,
 * and the canvas joins them into one line with their labels. Only an exact
 * repeat of the same label and destination is dropped.
 */
export const parseSupervisorRouting = (
  row: TableNode
): SupervisorRoutingBranch[] => {
  if (!SUPERVISOR_ROUTING_COMMANDS.has(String(row.command ?? '').trim())) {
    return [];
  }
  const input = asRecord(row.parameterInput);
  if (!input) return [];

  const branches: SupervisorRoutingBranch[] = [];
  const seen = new Set<string>();
  const add = (label: unknown, destination: unknown) => {
    if (typeof label !== 'string' || !label.trim()) return;
    const num =
      typeof destination === 'number'
        ? destination
        : typeof destination === 'string' && destination.trim() !== ''
          ? Number(destination)
          : NaN;
    if (!Number.isFinite(num)) return;
    const branch = { label: label.trim(), destination: String(num) };
    const key = `${branch.label}~${branch.destination}`;
    if (seen.has(key)) return;
    seen.add(key);
    branches.push(branch);
  };

  const destinations = asRecord(input.destination_nodes);
  if (destinations) {
    Object.entries(destinations).forEach(([label, dest]) => add(label, dest));
  }

  const listPicker = asRecord(input.static_listpicker_text);
  if (listPicker) {
    DISAMBIGUATION_LISTS.forEach((key) => {
      const entries = listPicker[key];
      if (!Array.isArray(entries)) return;
      entries.forEach((entry) => {
        const e = asRecord(entry);
        if (e) add(e.label, e.destination_node);
      });
    });
  }

  return branches;
};

/** What a destination walk resolved to — a real group (and the row the walk
 *  landed on inside it), or nothing (dangling, a cycle, the hop limit, or a
 *  dead end on an ungrouped row). */
type WalkResult =
  | { kind: 'group'; groupId: string; rowId: string }
  | { kind: 'none' };

/**
 * The node-level hop an agent-level edge stands for: the row the edge leaves
 * from and the row it lands on. `undefined` when more than one row produced
 * the same edge — there is then no single row to draw it from, so the canvas
 * falls back to the card. Drawing-only data: `buildCanvasEdges`' own output
 * is unchanged by it.
 */
export interface CanvasEdgeHop {
  sourceRowId?: string;
  targetRowId?: string;
}

/**
 * Walk from `startNodeNumber` via each intermediate node's `nextNodes`
 * pointer until the trail reaches a node owned by a group other than
 * `originGroup` — that group is the resolved destination.
 *
 * When the walk instead runs out — a dangling destination, a same-group-only
 * chain, an ungrouped dead end, a detected CYCLE (corrupt/self-referential
 * data), or the hop limit — it resolves to `'none'`. There is no real group
 * to draw an edge to.
 */
const resolveDestination = (
  startNodeNumber: string,
  originGroup: string,
  rowsByNumber: Map<string, TableNode>
): WalkResult => {
  let current: string | null = startNodeNumber;
  const visited = new Set<string>();

  for (let hop = 0; current !== null && hop < MAX_WALK_HOPS; hop += 1) {
    if (visited.has(current)) return { kind: 'none' }; // cycle — never crosses out
    visited.add(current);

    const row = rowsByNumber.get(current);
    if (!row) return { kind: 'none' }; // dangling destination — no such node number

    const rowGroup = groupIdOf(row);
    if (rowGroup !== null && rowGroup !== originGroup) {
      return { kind: 'group', groupId: rowGroup, rowId: row.id };
    }

    // Still in the origin's group (or an ungrouped intermediate) — advance
    // via THIS row's own sequential pointer, not the branch that got us
    // here; a branch only fires once, at the start of the walk.
    const next = (row.nextNodes ?? []).find(
      (n): n is number => n !== null && n !== undefined
    );
    current = next !== undefined ? String(next) : null;
  }

  return { kind: 'none' };
};

interface EdgeCandidate {
  originGroup: string;
  originRowId: string;
  dest: WalkResult;
  kind: CanvasEdgeKind;
  label?: string;
  inferred?: boolean;
}

const hasExamplePhrases = (row: TableNode): boolean =>
  typeof row.utterances === 'string' && row.utterances.trim() !== '';

const isDecisionRow = (row: TableNode): boolean =>
  String(row.nodeType ?? '').toLowerCase() === 'd';

/**
 * The Supervisor reaches a use-case task without a stored route: onboarding
 * creates each task as a Decision node whose example phrase is its
 * `utterances`, and the Supervisor routes by matching what a customer says
 * against those phrases (`task-utils.ts`'s `createTaskNode`). So every
 * grouped Decision node with example phrases, outside the Supervisor's own
 * group, gets an intent edge from the Supervisor's routing row, labelled with
 * the node's name and marked `inferred`.
 */
const addPhraseMatchedEdges = (
  rows: TableNode[],
  addEdge: (candidate: EdgeCandidate) => void
) => {
  const router = rows.find(
    (row) =>
      groupIdOf(row) !== null &&
      !isGroupRow(row) &&
      SUPERVISOR_ROUTING_COMMANDS.has(String(row.command ?? '').trim())
  );
  if (!router) return;
  const originGroup = groupIdOf(router) as string;

  rows.forEach((row) => {
    if (isGroupRow(row) || !isDecisionRow(row) || !hasExamplePhrases(row)) {
      return;
    }
    const groupId = groupIdOf(row);
    if (groupId === null || groupId === originGroup) return;
    addEdge({
      originGroup,
      originRowId: router.id,
      dest: { kind: 'group', groupId, rowId: row.id },
      kind: 'intent',
      label: String(row.nodeName ?? '').trim() || undefined,
      inferred: true,
    });
  });
};

const collectCanvasEdges = (
  rows: TableNode[]
): { edges: CanvasEdge[]; hops: Map<string, CanvasEdgeHop> } => {
  const rowsByNumber = new Map<string, TableNode>();
  rows.forEach((r) => {
    if (r.nodeNumber !== null && r.nodeNumber !== undefined) {
      rowsByNumber.set(String(r.nodeNumber), r);
    }
  });

  const edges: CanvasEdge[] = [];
  const seen = new Set<string>();
  const hops = new Map<string, CanvasEdgeHop>();

  const addEdge = (candidate: EdgeCandidate) => {
    const { originGroup, originRowId, dest, kind, label, inferred } = candidate;

    if (dest.kind !== 'group') return;
    if (dest.groupId === originGroup) return;
    const target = dest.groupId;

    const id = `${originGroup}->${target}:${kind}:${label ?? ''}`;
    if (seen.has(id)) {
      const hop = hops.get(id);
      if (hop && hop.sourceRowId !== originRowId) hop.sourceRowId = undefined;
      if (hop && hop.targetRowId !== dest.rowId) hop.targetRowId = undefined;
      return;
    }
    seen.add(id);
    hops.set(id, { sourceRowId: originRowId, targetRowId: dest.rowId });
    edges.push({
      id,
      source: originGroup,
      target,
      kind,
      label,
      ...(inferred ? { inferred: true } : {}),
    });
  };

  rows.forEach((row) => {
    const originGroup = groupIdOf(row);
    // An ungrouped row has no agent card on the canvas to draw an edge
    // from or to — nothing to resolve.
    if (originGroup === null) return;

    (row.nextNodes ?? []).forEach((n) => {
      if (n === null || n === undefined) return;
      const dest = resolveDestination(String(n), originGroup, rowsByNumber);
      addEdge({ originGroup, originRowId: row.id, dest, kind: 'flow' });
    });

    const managed = managedIntentNames(row);
    const branches = parseWhatsNext((row.whatsNext ?? []).join('|'));
    branches.forEach((branch) => {
      // An empty destination (no `~` in the branch) is a real, reachable
      // state — it just never produces a canvas edge.
      if (!branch.destination) return;
      const dest = resolveDestination(
        branch.destination,
        originGroup,
        rowsByNumber
      );
      const kind: CanvasEdgeKind = managed.has(branch.label)
        ? 'intent'
        : 'error';
      addEdge({
        originGroup,
        originRowId: row.id,
        dest,
        kind,
        label: branch.label,
      });
    });

    parseSupervisorRouting(row).forEach((branch) => {
      const dest = resolveDestination(
        branch.destination,
        originGroup,
        rowsByNumber
      );
      addEdge({
        originGroup,
        originRowId: row.id,
        dest,
        kind: 'intent',
        label: branch.label,
      });
    });
  });

  addPhraseMatchedEdges(rows, addEdge);

  return { edges, hops };
};

export const buildCanvasEdges = (rows: TableNode[]): CanvasEdge[] =>
  collectCanvasEdges(rows).edges;

/** Per edge id, the node rows it leaves from and lands on — see `CanvasEdgeHop`. */
export const buildCanvasEdgeHops = (
  rows: TableNode[]
): Map<string, CanvasEdgeHop> => collectCanvasEdges(rows).hops;

/**
 * Canvas Phase 3, build C — which agents count as "lit" once one is
 * selected: the selected agent itself, plus any agent an edge of ANY kind
 * connects it to (source or target). Everything else is what a click dims.
 *
 * Matches the live Protolab prototype's own `cvLit(id, eds)` exactly
 * (`prostudio-build-flow-layouts.html`, read this session): it checks `eds`
 * — the full edge list — not whatever subset is currently toggled visible,
 * so an agent joined to the selection only by a (currently hidden) error
 * edge still lights up rather than reading as unconnected.
 *
 * `selectedAgentId: null` returns `null` — "nothing is selected" is a
 * different state from "everything is lit," which the caller (deciding
 * whether to dim anything at all) needs to tell apart.
 */
export const litAgentIds = (
  selectedAgentId: string | null,
  edges: CanvasEdge[]
): Set<string> | null => {
  if (!selectedAgentId) return null;

  const lit = new Set<string>([selectedAgentId]);
  edges.forEach((edge) => {
    if (edge.source === selectedAgentId) lit.add(edge.target);
    if (edge.target === selectedAgentId) lit.add(edge.source);
  });
  return lit;
};
